// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/await-thenable, @typescript-eslint/no-explicit-any, @typescript-eslint/no-floating-promises, @typescript-eslint/no-misused-promises, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, import/no-named-as-default, prefer-const, react-hooks/exhaustive-deps, react-hooks/immutability */

import produce from "immer";
import { WritableDraft } from "immer/dist/internal";
import _ from "lodash";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button, Form, Grid, Loader, Segment, Table } from "semantic-ui-react";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import * as trialActions from "../../../../redux/actions/trialActions";
import TreatmentCSVUpload from "../../../Lumber/TreatmentCSVUpload";
import * as editTrialAuth from "../EditTrial/editTrialAuth";

type CSVTreatment = {
	description?: string;
	name?: string;
	type?: string;
	trialTreatmentId: number;
	seed?: string;
};

type Treatment = {
	treatmentDescription?: string;
	treatmentId: string;
	treatmentName?: string;
	treatmentTypeDescription?: string;
	treatmentTypeId?: string;
	treatmentTypeName?: string;
	trialTreatmentId: number;
	seed?: string;
};

type TreatmentTypeOption = {
	key: string;
	value: string;
	text: string;
};

type ValidationMessage = {
	id: string;
	name: boolean;
	description: boolean;
	seed: boolean;
	message: string;
};

type Validation = {
	isValid: boolean;
	treatments: { messages: ValidationMessage[] };
	messages: string[];
};

type Props = {
	trialId: string;
	trialName: string;
	initTreatments: Treatment[];
	navigateToNextStep: () => void;
	navigateToPreviousStep: () => void;
	setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
	setSaveAction: React.Dispatch<React.SetStateAction<() => Promise<any>>>;
};

const TrialTreatments: FC<Props> = ({
	trialId,
	trialName,
	initTreatments,
	navigateToNextStep,
	navigateToPreviousStep,
	setIsDirty,
	setSaveAction,
}) => {
	const userAuth = useUserAuth();
	const { getTokenSilently } = useAuth0();
	const moduleNavigation = useModuleNavigation();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [defaultTrialTypeId, setDefaultTrialTypeId] = useState<string>();

	const [treatments, setTreatmentsSilently] = useState(initTreatments ?? []);
	// const [treatmentsAreVaild, setTreatmentsArevValid] = useState();
	// const [test, setTest] = useState<TreatmentTypeOption>();
	const [treatmentTypeOptions, setTreatmentTypeOptions] = useState<TreatmentTypeOption[]>([]);

	const save = useCallback(async () => {
		setIsSaving(true);

		const validation = validateTreatments();
		const treatmentsToSave = treatments.reduce((acc, tr) => {
			if (tr.treatmentTypeId === null) {
				tr.treatmentTypeId = defaultTrialTypeId;
			}

			// if (!!tr.treatmentName?.trim()) {
			acc.push(tr);
			// } else {
			// 	ignoredBlank = true;
			// }

			return acc;
		}, [] as Treatment[]);

		if (!validation.isValid) {
			setIsSaving(true);
			toast.error("Failed to save.");
			// for (let message of validation.treatments.messages) {
			// 	toast.error(message);
			// }
			return false;
		}

		try {
			const accessToken = await getTokenSilently();
			await dispatch(trialActions.updateTrialTreatments(userAuth.currentClientId, treatmentsToSave, accessToken));
			setIsSaving(false);
			setIsDirty(false);
			toast.success("Treatments saved successfully.");
			return true;
		} catch (err) {
			setIsSaving(false);
			console.error(err);
			toast.error("Trial Treatments failed to save.");
			return false;
		}
	}, [treatments, userAuth.currentClientId]);

	useEffect(() => {
		if (userAuth.currentClientId && moduleNavigation.trialId) {
			getTrialOptions(userAuth.currentClientId);
		}
	}, [userAuth.currentClientId]);

	useEffect(() => {
		setSaveAction(() => save);
	}, [save]);

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const setTreatments = (tmnts) => {
		setIsDirty(true);
		setTreatmentsSilently(tmnts);
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const getTrialOptions = async (clientId) => {
		const accessToken = await getTokenSilently();
		setLoading(true);
		trialActions
			.getTrialOptions(
				clientId,
				moduleNavigation.trialId,
				accessToken,
			)(dispatch)
			.then((res) => {
				initializeTreatmentTypeOptions(res.treatmentTypes);
				setLoading(false);
			})
			.catch((err) => {
				toast.error("Error loading trial options.");
				console.log(err);
				setLoading(false);
			});
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const initializeTreatmentTypeOptions = (treatmentTypes) => {
		const treatmentTypeOpts = _.map(treatmentTypes, (tt) => {
			if (tt.name === "Test") {
				setDefaultTrialTypeId(tt.id);
			}
			const opt = {
				key: tt.id,
				value: tt.id,
				text: tt.name,
			};
			return opt;
		});
		setTreatmentTypeOptions(treatmentTypeOpts);
	};

	const validateTreatments = useCallback(() => {
		let validation: Validation = {
			isValid: true,
			treatments: { messages: [] },
			messages: [],
		};

		treatments.forEach((treatment) => {
			if (treatment.treatmentName && treatment.treatmentName.length > 50) {
				validation.treatments.messages.push({
					id: treatment.treatmentId,
					name: true,
					description: false,
					seed: false,
					message: "Treatment Name cannot be more than 50 characters.",
				});
				validation.isValid = false;
			}

			if (treatment.treatmentDescription && treatment.treatmentDescription.length > 200) {
				const err = {
					id: treatment.treatmentId,
					name: false,
					description: true,
					seed: false,
					message: "Treatment Description cannot be more than 200 characters.",
				};
				validation.treatments.messages.push(err);
				validation.isValid = false;
			}

			if (treatment.seed && treatment.seed.length > 50) {
				validation.treatments.messages.push({
					id: treatment.treatmentId,
					name: false,
					description: false,
					seed: true,
					message: "Seed cannot be more than 50 characters.",
				});
				validation.isValid = false;
			}
		});

		if (validation.treatments.messages.length === 0) {
			validation.messages.push("Treatment errors were found. Please review the treatment list.");
		}

		return validation;
	}, []);

	function saveTreatmentsFromValidCSV(results: CSVTreatment[]) {
		const tmnts = results.map((r, idx) => {
			const treatment = treatments.find((t) => t.trialTreatmentId === idx + 1) as Treatment;
			treatment.treatmentName = r.name;
			const typeId = r.type
				? treatmentTypeOptions.find((tto) => tto.text.toUpperCase() === r.type!.toUpperCase())?.value
				: undefined;
			treatment.treatmentTypeId = typeId;
			treatment.treatmentDescription = r.description;
			treatment.seed = r.seed;
			return treatment;
		});

		setTreatments([...tmnts]);

		save();
	}

	function clearTreatments() {
		const newTreatments = produce(treatments, (draft) => {
			draft = draft.map((_r, idx) => {
				const treatment = draft.find((t) => t.trialTreatmentId === idx + 1) as WritableDraft<Treatment>;
				treatment.treatmentName = "";
				treatment.treatmentTypeId = undefined;
				treatment.treatmentDescription = "";
				treatment.seed = "";
				return treatment;
			});
		});

		setTreatments(newTreatments);
	}

	return loading ? (
		<Loader active />
	) : (
		<>
			<div style={{ marginBottom: "1em" }}>
				<div style={{ display: "flex", alignItems: "end", overflow: "hidden" }}>
					<Button
						style={{ marginRight: 10 }}
						compact
						color="black"
						content="Back"
						onClick={() => {
							navigateToPreviousStep();
						}}
					/>
					<div style={{ flexGrow: 1, minWidth: 10 }} />
					<Button
						id="form-button-save"
						compact
						primary
						content="Save"
						loading={loading || isSaving}
						disabled={loading || isSaving}
						onClick={() => save()}
					/>
					<Button
						id="form-button-continue"
						compact
						positive
						content="Save and Continue"
						loading={loading || isSaving}
						disabled={loading || isSaving}
						onClick={() =>
							save().then((res) => {
								if (res) navigateToNextStep();
							})
						}
					/>
				</div>
			</div>
			<Segment basic style={{ marginTop: 0, padding: 0 }}>
				<Form>
					<Grid>
						<Grid.Row style={{ padding: 0 }}>
							<Grid.Column>
								<TreatmentCSVUpload
									hasImportedData={true}
									onAcceptCSV={saveTreatmentsFromValidCSV}
									onClearTreatments={clearTreatments}
									context="trial"
									trialData={{
										id: trialId,
										name: trialName,
										treatmentCount: treatments.length,
										clientId: userAuth.currentClientId,
										context: "trial",
									}}
									currentData={(() => {
										return treatments?.map((t) => ({
											Treatment: t.trialTreatmentId,
											Name: t.treatmentName,
											Type: treatmentTypeOptions?.find((tto) => tto.value === t.treatmentTypeId)?.text,
											Description: t.treatmentDescription,
											Seed: t.seed,
										}));
									})()}
								/>
							</Grid.Column>
						</Grid.Row>
						<Grid.Row>
							<Grid.Column style={{ maxHeight: 500, overflowY: "auto", padding: 0, margin: "0 1em" }}>
								<Table
									id="form-table-treatment-listing"
									celled
									striped
									sortable
									selectable
									color="blue"
									textAlign="center"
								>
									<Table.Header>
										<Table.Row>
											<Table.HeaderCell width={1}>Treatment</Table.HeaderCell>
											<Table.HeaderCell width={6}>Name</Table.HeaderCell>
											<Table.HeaderCell width={2}>Type</Table.HeaderCell>
											<Table.HeaderCell width={5}>Description</Table.HeaderCell>
											<Table.HeaderCell width={2}>Seed</Table.HeaderCell>
										</Table.Row>
									</Table.Header>

									<Table.Body>
										{treatments.map(
											({
												treatmentId,
												trialTreatmentId,
												treatmentName,
												treatmentTypeId,
												treatmentDescription,
												seed,
											}) => {
												return (
													<Table.Row key={treatmentId}>
														<Table.Cell>{trialTreatmentId}</Table.Cell>
														<Table.Cell>
															<Form.Input
																fluid
																key={treatmentId}
																id={`form-input-treatment-name-${treatmentId}`}
																value={treatmentName ?? ""}
																onChange={(e) => {
																	const newTreatments = _.map(treatments, (tr) => {
																		if (tr.treatmentId === treatmentId) {
																			return { ...tr, treatmentName: e.target.value };
																		}
																		return tr;
																	});
																	setTreatments(newTreatments);
																}}
																// error={!!trialValidation?.treatments?.messages.find((t) => t.id === treatmentId && t.name)}
																readOnly={!editTrialAuth.userCanEditTreatments(userAuth)}
																style={{ opacity: 1, color: "#000000" }}
															/>
														</Table.Cell>
														<Table.Cell>
															<Form.Select
																fluid
																id={`form-select-treatment-type-${treatmentId}`}
																options={treatmentTypeOptions}
																value={treatmentTypeId ?? undefined}
																placeholder="Select"
																onChange={(_e, { value }) => {
																	const newTreatments = _.map(treatments, (tr) => {
																		if (tr.treatmentId === treatmentId) {
																			return { ...tr, treatmentTypeId: value as string };
																		}
																		return tr;
																	});
																	setTreatments(newTreatments);
																}}
																loading={!treatmentTypeOptions || treatmentTypeOptions.length === 0}
																disabled={!editTrialAuth.userCanEditTreatments(userAuth)}
																style={{ opacity: 1, color: "#000000" }}
															/>
														</Table.Cell>
														<Table.Cell>
															<Form.Input
																fluid
																key={treatmentId}
																id={`form-input-treatment-description-${treatmentId}`}
																value={treatmentDescription ?? ""}
																onChange={(e) => {
																	const newTreatments = _.map(treatments, (tr) => {
																		if (tr.treatmentId === treatmentId) {
																			return { ...tr, treatmentDescription: e.target.value };
																		}
																		return tr;
																	});
																	setTreatments(newTreatments);
																}}
																// error={
																// 	!!trialValidation?.treatments?.messages.find((t) => t.id === treatmentId && t.description)
																// }
																readOnly={!editTrialAuth.userCanEditTreatments(userAuth)}
																style={{ opacity: 1, color: "#000000" }}
															/>
														</Table.Cell>
														<Table.Cell>
															<Form.Input
																fluid
																key={`${treatmentId}-seed`}
																id={`form-input-treatment-seed-${treatmentId}`}
																value={seed ?? ""}
																onChange={(e) => {
																	const newTreatments = _.map(treatments, (tr) => {
																		if (tr.treatmentId === treatmentId) {
																			return { ...tr, seed: e.target.value };
																		}
																		return tr;
																	});
																	setTreatments(newTreatments);
																}}
																readOnly={!editTrialAuth.userCanEditTreatments(userAuth)}
																style={{ opacity: 1, color: "#000000" }}
															/>
														</Table.Cell>
													</Table.Row>
												);
											},
										)}
									</Table.Body>
								</Table>
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</Form>
			</Segment>
		</>
	);
};

export default TrialTreatments;
